
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { TeamMember } from '@/types/database';
import { emailService } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { nanoid } from 'nanoid';

export function useTeamMembers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isInviting, setIsInviting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const userId = user?.id;
  
  const generateInviteToken = () => {
    // Generate a secure token without any special characters to avoid encoding issues
    return nanoid(32);
  };

  const { data: teamMembers, isLoading, refetch } = useQuery({
    queryKey: ['team-members', userId],
    queryFn: async () => {
      if (!userId) return [];
      try {
        console.log('Fetching team members for user:', userId);
        
        const { data, error } = await supabase
          .from('team_members')
          .select('*')
          .or(`invited_by.eq.${userId},user_id.eq.${userId}`)
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching team members:', error);
          throw error;
        }
        
        console.log(`Found ${data?.length || 0} team members`);
        return data as TeamMember[];
      } catch (error) {
        console.error('Error in team members query:', error);
        return [];
      }
    },
    enabled: !!userId,
  });

  const getUserOrganization = async () => {
    if (!userId) {
      throw new Error('User is not authenticated');
    }
    
    try {
      // Explicitly passing both parameters to avoid function ambiguity
      const { data, error } = await supabase.rpc(
        'get_user_organization',
        { 
          user_id: userId,
          user_email: user?.email || '' 
        }
      );
      
      if (error) {
        console.error('Error getting user organization:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        throw new Error('You need to create an organization before inviting team members');
      }
      
      return data[0];
    } catch (error: any) {
      console.error('Error getting organization:', error);
      throw new Error(error.message || 'Failed to get organization');
    }
  };

  const inviteTeamMember = async ({ email, role }: { email: string, role: 'admin' | 'member' }) => {
    if (!userId) {
      throw new Error('User is not authenticated');
    }
    
    setIsInviting(true);
    
    try {
      const normalizedEmail = email.toLowerCase();
      
      const existingInvite = teamMembers?.find(
        member => member.email.toLowerCase() === normalizedEmail
      );
      
      if (existingInvite) {
        if (existingInvite.status === 'active') {
          throw new Error(`${email} is already a team member`);
        } else {
          throw new Error(`${email} has already been invited`);
        }
      }
      
      const organization = await getUserOrganization();
      console.log('User organization:', organization);
      
      const inviteToken = generateInviteToken();
      console.log(`Generated invite token with length: ${inviteToken.length}`);
      
      const { data: invitation, error: inviteError } = await supabase
        .from('team_members')
        .insert({
          email: normalizedEmail,
          role,
          invited_by: userId,
          status: 'pending',
          user_id: userId,
          invite_token: inviteToken,
          organization_id: organization.id
        })
        .select()
        .single();
        
      if (inviteError) {
        console.error('Error creating invitation:', inviteError);
        throw new Error(`Failed to create invitation: ${inviteError.message}`);
      }
      
      console.log('Created invitation:', invitation);
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', userId)
        .single();
        
      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }
      
      const inviterName = profile ? 
        `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 
        'A team administrator';
        
      console.log(`Sending invitation email to ${normalizedEmail}`);
      
      try {
        console.log('Email service request:', {
          to: normalizedEmail,
          inviterName: inviterName || undefined,
          role
        });
        
        const { data: emailData, error: emailError } = await emailService.sendInvitation({
          to: normalizedEmail,
          inviterName: inviterName || undefined,
          inviteToken,
          role
        });
        
        console.log('Email service response:', { data: emailData, error: emailError });
        
        if (emailError) {
          console.error('Error sending invitation email:', emailError);
          toast.error(`Invitation created but email delivery failed: ${emailError.message || 'Unknown error'}`);
        } else {
          toast.success(`Invitation sent to ${normalizedEmail}`);
          console.log('Email sent successfully');
        }
      } catch (emailSendError: any) {
        console.error('Exception during email sending:', emailSendError);
        toast.error(`Invitation created but email delivery failed: ${emailSendError.message || 'Unknown error'}`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['team-members', userId] });
      
      return invitation;
    } catch (error: any) {
      console.error('Error inviting team member:', error);
      throw new Error(error.message || 'Failed to invite team member');
    } finally {
      setIsInviting(false);
    }
  };

  const removeTeamMember = useMutation({
    mutationFn: async (memberId: string) => {
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      
      setIsRemoving(true);
      
      try {
        const { error } = await supabase
          .from('team_members')
          .delete()
          .eq('id', memberId)
          .eq('invited_by', userId);
          
        if (error) {
          console.error('Error removing team member:', error);
          throw new Error(`Failed to remove team member: ${error.message}`);
        }
        
        return { success: true };
      } catch (error: any) {
        console.error('Error in remove team member:', error);
        throw new Error(error.message || 'Failed to remove team member');
      } finally {
        setIsRemoving(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', userId] });
    }
  });

  const updateTeamMemberRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string, role: 'admin' | 'member' }) => {
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      
      try {
        const { error } = await supabase
          .from('team_members')
          .update({ role })
          .eq('id', memberId)
          .eq('invited_by', userId);
          
        if (error) {
          console.error('Error updating team member role:', error);
          throw new Error(`Failed to update role: ${error.message}`);
        }
        
        return { success: true };
      } catch (error: any) {
        console.error('Error in update team member role:', error);
        throw new Error(error.message || 'Failed to update role');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', userId] });
    }
  });

  const resendInvitation = async (memberId: string) => {
    if (!userId) {
      throw new Error('User is not authenticated');
    }
    
    setIsResending(true);
    
    try {
      const { data: invitation, error: inviteError } = await supabase
        .from('team_members')
        .select('*')
        .eq('id', memberId)
        .eq('status', 'pending')
        .single();
        
      if (inviteError || !invitation) {
        console.error('Error fetching invitation:', inviteError);
        throw new Error('Failed to find the invitation');
      }
      
      const newToken = generateInviteToken();
      console.log(`Generated new invite token with length: ${newToken.length}`);
      
      const { error: updateError } = await supabase
        .from('team_members')
        .update({ 
          invite_token: newToken,
          updated_at: new Date().toISOString()
        })
        .eq('id', memberId);
        
      if (updateError) {
        console.error('Error updating invitation token:', updateError);
        throw new Error(`Failed to update invitation: ${updateError.message}`);
      }
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', userId)
        .single();
        
      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }
      
      const inviterName = profile ? 
        `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 
        'A team administrator';
      
      console.log(`Resending invitation email to ${invitation.email}`);
      
      try {
        console.log('Email resend request:', {
          to: invitation.email,
          inviterName: inviterName || undefined,
          role: invitation.role
        });
        
        const { data: emailData, error: emailError } = await emailService.sendInvitation({
          to: invitation.email,
          inviterName: inviterName || undefined,
          inviteToken: newToken,
          role: invitation.role
        });
        
        console.log('Email resend response:', { data: emailData, error: emailError });
        
        if (emailError) {
          console.error('Error sending invitation email:', emailError);
          throw new Error(`Failed to send invitation email: ${emailError.message}`);
        } else {
          toast.success(`Invitation resent to ${invitation.email}`);
          console.log('Email resent successfully');
        }
      } catch (emailResendError: any) {
        console.error('Exception during email resending:', emailResendError);
        throw new Error(`Failed to send invitation email: ${emailResendError.message || 'Unknown error'}`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['team-members', userId] });
      
      return { success: true };
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      throw new Error(error.message || 'Failed to resend invitation');
    } finally {
      setIsResending(false);
    }
  };

  const testInvitationEmail = async () => {
    if (!userId) {
      toast.error("User is not authenticated");
      return { success: false };
    }
    
    try {
      const testEmail = 'test@example.com';
      const testToken = generateInviteToken();
      
      console.log(`TEST: Sending invitation email to ${testEmail}`);
      
      const { data, error } = await emailService.sendInvitation({
        to: testEmail,
        inviterName: 'Test User',
        inviteToken: testToken,
        role: 'member'
      });
      
      console.log('TEST: Email service direct test response:', { data, error });
      
      if (error) {
        toast.error(`TEST: Email sending failed: ${error.message || 'Unknown error'}`);
        return { success: false, error };
      }
      
      toast.success(`TEST: Email sent to ${testEmail}`);
      return { success: true, data };
    } catch (error: any) {
      console.error('TEST: Error in test email send:', error);
      toast.error(`TEST: Email test failed: ${error.message || 'Unknown error'}`);
      return { success: false, error };
    }
  };

  const refetchTeamMembers = () => {
    return refetch();
  };

  return {
    teamMembers,
    isLoading,
    inviteTeamMember,
    removeTeamMember,
    updateTeamMemberRole,
    resendInvitation,
    isInviting,
    isRemoving,
    isResending,
    refetchTeamMembers,
    testInvitationEmail
  };
}
